import sortBy from 'lodash/sortBy';
import between from '@inquisitive/between';
import { generateText, JSONContent } from '@tiptap/core';

import {
  InquisitiveActivitiesComponentType,
  JSONObject,
} from 'global/graphql/scalars.type';
import type { InteractiveLessonSectionComponentFragment } from '../../lesson-builder.graphql.generated';
import type { SubActivityDetailsInput } from '../../../../gql/schema-types';
import {
  SubActivitiesLabelType,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_TABLE,
  ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_DRAW,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE,
} from 'global/constants';
import {
  ComponentActivity,
  ComponentSubActivity,
} from '../component.constants';
import type { InteractiveSubActivityFragment } from '../../lesson-builder.graphql.generated';
import type { InteractiveLessonSectionFragment } from '../../lesson-builder.graphql.generated';
import { findActivity } from '../activity/activity.utils';
import {
  getExtensions,
  isRichtextEmpty,
} from 'approot/shared/richtext/richtext.utils';
import { SUB_ACTIVITY_INSTRUCTION_SETTINGS } from './sub-activity.constants';

export function formatSubActivityLabel(
  label: string | null | undefined
): string {
  return label ? `${label}. ` : ' ';
}

export function getSubActivityFullLabel(
  type: InquisitiveActivitiesComponentType
): string {
  switch (type) {
    case ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE:
      return 'Free Text Response';
    case ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE:
      return 'Multiple Choice';
    case ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE:
      return 'Equation Response';
    case ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE:
      return 'Fill in the Blanks';
    case ACTIVITIES_COMPONENT_TYPE_TABLE:
      return 'Table';
    case ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE:
      return 'Class Discussion Response';
    case ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE:
      return 'Matching or Sequencing Response';
    case ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE:
      return 'Grouping Response';
    case ACTIVITIES_COMPONENT_TYPE_DRAW:
      return 'Drawing';
    default:
      return '';
  }
}

export function sortSubActivities(
  subActivities: InteractiveLessonSectionComponentFragment[] | undefined
): ComponentSubActivity[] {
  return sortBy(
    subActivities,
    'subActivityProperties.subActivitySort'
  ) as ComponentSubActivity[];
}

export function getSubActivityInput(
  section: InteractiveLessonSectionFragment | undefined,
  showInstructions = true
): {
  subActivityInput: SubActivityDetailsInput;
  optimisticResponse: JSONObject;
  activity?: ComponentActivity;
} {
  const activity = findActivity(section);
  const subActivities = sortSubActivities(activity?.subActivities || []);

  return {
    subActivityInput: {
      areInstructionsVisible: showInstructions,
      subActivitySort: between.between(
        subActivities.at(-1)?.subActivityProperties?.subActivitySort ||
          between.lowest,
        between.highest
      ),
    },
    optimisticResponse: {
      activityComponentId: activity?.id,
      subActivityLabel: getSubActivityLabel(
        activity?.properties.subActivitiesLabelType,
        subActivities.length
      ),
      instructions: null,
      previews: [],
      __typename: 'SubActivityComponentProperties',
    },
    activity,
  };
}

export function getSubActivityLabel(
  type: SubActivitiesLabelType | null | undefined,
  index: number
): string | number | null {
  if (type === 'alphabetical') {
    return 'abcdefghijklmnopqrstuvwxyz'.charAt(index);
  } else if (type === 'numerical') {
    return index + 1;
  }
  return null;
}

export function getSubActivityPayload(
  data: InteractiveSubActivityFragment | null
): { subActivityDetails: SubActivityDetailsInput } | undefined {
  return !data
    ? undefined
    : {
        subActivityDetails: {
          subActivitySort: data.subActivitySort,
          areInstructionsVisible: data.areInstructionsVisible,
          instructions: data.instructions,
        },
      };
}

export function getSubActivityInstructionAsText(
  instructions: JSONContent | null | undefined
): string | null | undefined {
  return instructions && !isRichtextEmpty(instructions)
    ? generateText(
        instructions,
        getExtensions(SUB_ACTIVITY_INSTRUCTION_SETTINGS)
      )
    : '';
}
